import type { ReactNode, SetStateAction } from 'react';
import { useEffect, Fragment } from 'react';

import SwiperCore, { Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { ArrowRightBigIcon } from '@setup/svg-icons/svg-icons';
import { useSwiperLogic } from '@shared/hooks/useSwiperLogic';

import { SwiperContainer, SwiperNextButton, SwiperPrevButton } from './SwiperContainerWithButtons.style';

SwiperCore.use([Navigation, Pagination]);

type SizeButtons = 'large' | 'medium';
export type ButtonConfig = Partial<{ top: string; left: string; right: string; bottom: string }>;

type ButtonsConfig = Partial<{
  leftButton: ButtonConfig;
  rightButton: ButtonConfig;
}>;

type PropsType<T> = {
  slidesPerView?: number | 'auto';
  spaceBetween?: number;
  renderItem: (item: T, index?: number) => ReactNode;
  items: T[];
  buttonsConfig?: ButtonsConfig;
  size?: SizeButtons;
  allowTouchMove?: boolean;
  triggerNextPage?: () => void;
  slidesPerGroup?: number;
};

export const SwiperContainerWithButtons = <T extends { id: number | string }>(props: PropsType<T>) => {
  const {
    slidesPerView = 'auto',
    spaceBetween = 16,
    items,
    renderItem,
    size = 'large',
    buttonsConfig,
    allowTouchMove = true,
    triggerNextPage,
    slidesPerGroup,
  } = props;

  const {
    isClickedNextBtn,
    isClickedPrevBtn,
    swiperRef,
    setNextEl,
    setPrevEl,
    isEnd,
    isBegin,
    handleClickPrevBtn,
    handleClickNextBtn,
    handleReachBeginning,
    handleReachEnd,
    isShowNavigation,
  } = useSwiperLogic();

  const sliders = items.map((item, index) => (
    <SwiperSlide key={`${item.id}-${index}`}>
      <Fragment key={`${item.id}-${index}`}>{renderItem(item, index)}</Fragment>
    </SwiperSlide>
  ));

  useEffect(() => {
    if (!triggerNextPage) return;
    if (triggerNextPage && !isBegin && isEnd) {
      triggerNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnd]);
  return (
    <SwiperContainer>
      <Swiper
        ref={swiperRef}
        slidesPerView={slidesPerView}
        slidesPerGroup={slidesPerGroup}
        loop={false}
        allowTouchMove={allowTouchMove}
        spaceBetween={spaceBetween}
        onReachEnd={handleReachEnd}
        onReachBeginning={handleReachBeginning}
        modules={[Pagination, Navigation]}
        className="mySwiper"
      >
        {sliders}
      </Swiper>

      {isShowNavigation && (
        <>
          <SwiperNextButton
            size={size}
            rightConfig={buttonsConfig?.rightButton}
            ref={(node: SetStateAction<HTMLElement | null>) => setNextEl(node)}
            isFirstSlided={isEnd}
            isClicked={isClickedNextBtn}
            onClick={handleClickNextBtn}
          >
            <ArrowRightBigIcon />
          </SwiperNextButton>

          <SwiperPrevButton
            size={size}
            leftConfig={buttonsConfig?.leftButton}
            isLastSlided={isBegin}
            isClicked={isClickedPrevBtn}
            onClick={handleClickPrevBtn}
            ref={(node: SetStateAction<HTMLElement | null>) => setPrevEl(node)}
            sx={{ svg: { transform: 'rotate(180deg)' } }}
          >
            <ArrowRightBigIcon />
          </SwiperPrevButton>
        </>
      )}
    </SwiperContainer>
  );
};
